import React, { useState } from "react";
import api from "@/lib/api";
import { StarRating } from "@/components/reviews/StarRating";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { BadgeCheck, CornerDownRight, Loader2, Store } from "lucide-react";

function fmtDate(at) {
    try { return new Date(at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
    catch { return ""; }
}

const SUB_LABELS = {
    item_as_described: "Item as described",
    shipping_speed: "Shipping speed",
    communication: "Communication",
};

export function ReviewCard({ review, canReply = false, onReplied }) {
    const [open, setOpen] = useState(false);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const scores = review.scores || {};

    const submitReply = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/reviews/${review.id}/reply`, { text });
            toast.success("Reply posted");
            setOpen(false); setText("");
            onReplied?.(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not post reply");
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="review-card" className="rounded-xl border border-border bg-card p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent text-xs font-bold text-electric">
                        {(review.buyer_name || "B").slice(0, 2).toUpperCase()}
                    </span>
                    <div>
                        <p className="flex items-center gap-1.5 text-sm font-semibold">
                            {review.buyer_name}
                            <Badge variant="secondary" className="gap-1 px-1.5 py-0 text-[10px]"><BadgeCheck className="h-3 w-3 text-success" />Verified buyer</Badge>
                        </p>
                        <p className="text-xs text-muted-foreground">{fmtDate(review.created_at)}{review.auction_title ? ` · ${review.auction_title}` : ""}</p>
                    </div>
                </div>
                <StarRating value={review.overall} showValue size="md" />
            </div>

            {/* Sub-scores */}
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                {Object.keys(SUB_LABELS).map((k) => (
                    <div key={k} className="flex items-center justify-between rounded-lg border border-border bg-secondary/30 px-3 py-1.5">
                        <span className="text-xs text-muted-foreground">{SUB_LABELS[k]}</span>
                        <StarRating value={scores[k]} size="sm" />
                    </div>
                ))}
            </div>

            {review.comment && <p className="mt-3 text-sm leading-relaxed">{review.comment}</p>}

            {/* Seller reply */}
            {review.seller_reply && (
                <div className="mt-3 rounded-lg border border-electric/20 bg-electric/[0.05] p-3" data-testid="review-seller-reply">
                    <p className="flex items-center gap-1.5 text-xs font-semibold text-electric"><Store className="h-3.5 w-3.5" />Seller response</p>
                    <p className="mt-1 text-sm">{review.seller_reply.text}</p>
                </div>
            )}

            {/* Reply action (owner only, one reply) */}
            {canReply && !review.seller_reply && (
                <div className="mt-3">
                    {!open ? (
                        <Button size="sm" variant="secondary" className="rounded-[10px]" onClick={() => setOpen(true)} data-testid="review-reply-button">
                            <CornerDownRight className="mr-1.5 h-3.5 w-3.5" />Reply
                        </Button>
                    ) : (
                        <div className="space-y-2">
                            <Textarea
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                placeholder="Respond professionally. No links, emails, or phone numbers."
                                rows={3}
                                data-testid="review-reply-input"
                            />
                            <div className="flex gap-2">
                                <Button size="sm" className="rounded-[10px]" disabled={!text.trim() || busy} onClick={submitReply} data-testid="review-reply-submit">
                                    {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}Post reply
                                </Button>
                                <Button size="sm" variant="ghost" className="rounded-[10px]" onClick={() => { setOpen(false); setText(""); }}>Cancel</Button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
